import type {
  ScorecardData,
  OverallEvaluationValue,
} from '@kula-ai/components';
import type { ScorecardType } from '@/types/interview';

/**
 * Form values for a scorecard fill: answers are keyed by 1-based question index
 * (`values[idx + 1]`) — matching the legacy FormBuilder config — plus `feedback`
 * and `rating`. Attachment lists live under `${idx + 1}_attachments` /
 * `feedback_attachments` but are uploaded separately and never enter the submit
 * payload.
 */
export type ScorecardFillValues = Record<string, unknown>;

type AttachmentItems = NonNullable<
  ScorecardData['questions'][number]['attachments']
>;

/**
 * Maps the form's attachment records (`values[`${idx + 1}_attachments`]` /
 * `values.feedback_attachments`) into the builder's `AttachmentItem[]`. Handles
 * both freshly-picked `File`s and persisted server records (`AttachmentType`).
 */
const mapToAttachmentItems = (records: unknown): AttachmentItems => {
  if (!Array.isArray(records)) return [];
  return records.map((record) => {
    if (typeof File !== 'undefined' && record instanceof File) {
      return {
        key: `${record.name}-${record.size}`,
        name: record.name,
        size: record.size,
        mimeType: record.type,
        file: record,
      };
    }
    const r = record as {
      id?: number | string;
      name?: string;
      filename?: string;
      size?: number;
      byte_size?: number;
      content_type?: string;
      url?: string;
    };
    return {
      key: String(
        r.id ?? `${r.name ?? r.filename ?? ''}-${r.size ?? r.byte_size ?? ''}`
      ),
      name: r.name ?? r.filename ?? '',
      size: r.size ?? r.byte_size,
      mimeType: r.content_type,
      url: r.url,
    };
  });
};

/**
 * Builds the kula-ai `ScorecardBuilder` `data` for FILL mode from the live form
 * values. Answers come from `values[idx + 1]`; value types already match the
 * native atoms (star/one_to_ten → number, yes_no/multiple_choice → string,
 * checkbox → string[], open_question → HTML string), so nothing is coerced.
 * Attachments for the native `open_question` / feedback editors are mapped from
 * `values[`${idx + 1}_attachments`]` / `values.feedback_attachments`.
 */
export const buildFillData = (
  values: ScorecardFillValues,
  scorecard: ScorecardType
): ScorecardData => {
  const {
    questions = [],
    feedback_description,
    rating_description,
  } = scorecard ?? ({} as ScorecardType);

  return {
    questions: questions.map((question, idx) => ({
      question: question.question,
      description: question.description,
      type: question.type,
      answer: (values?.[idx + 1] ??
        '') as ScorecardData['questions'][number]['answer'],
      options:
        question.type === 'yes_no'
          ? (question.options ?? ['Yes', 'No'])
          : question.options,
      mandatory: question.mandatory === true || question.mandatory === 'true',
      ...(question.type === 'open_question'
        ? {
            attachments: mapToAttachmentItems(
              values?.[`${idx + 1}_attachments`]
            ),
          }
        : {}),
    })),
    feedback: (values?.feedback as string) ?? '',
    feedbackDescription: feedback_description,
    feedbackAttachments: mapToAttachmentItems(values?.feedback_attachments),
    rating: ((values?.rating as string) ||
      null) as OverallEvaluationValue | null,
    ratingDescription: rating_description,
  };
};

interface FillFieldConfig {
  name: string | number;
  [key: string]: unknown;
}

/**
 * Builds the FILL `data` for `ScorecardBuilder` **and** sets the
 * `feedbackMandatory` / `ratingMandatory` flags that `validateScorecard`
 * needs. The flags mirror the surface's config: feedback is required when the
 * scorecard marks it mandatory and the field is shown; rating is required
 * whenever the rating section is shown (the BE 422s on a blank rating at
 * submit). Per-question `mandatory` already comes from `buildFillData`.
 */
export const buildScorecardFillData = (
  values: ScorecardFillValues,
  scorecard: ScorecardType,
  formConfig: FillFieldConfig[] = []
): ScorecardData => {
  const showFeedback = formConfig.some((f) => String(f.name) === 'feedback');
  const showRating = formConfig.some((f) => String(f.name) === 'rating');

  return {
    ...buildFillData(values, scorecard),
    feedbackMandatory:
      showFeedback && Boolean(scorecard?.is_feedback_mandatory),
    ratingMandatory: showRating,
  };
};

export interface ScorecardSubmitQuestion {
  question: string;
  answer?: string | number | string[];
}

export interface ScorecardSubmitPayload {
  rating: unknown;
  feedback: unknown;
  status: 'draft' | 'submitted';
  questions: ScorecardSubmitQuestion[];
}

/**
 * Maps fill form values to the scorecard submit payload — kept BYTE-IDENTICAL to
 * the legacy serializers: `questions` carry only `{ question, answer? }` (answer
 * omitted when falsy), plus top-level `rating`, `feedback`, `status`. Attachments
 * are NOT included (uploaded out of band).
 */
export const mapScorecardValuesToPayload = (
  values: ScorecardFillValues,
  scorecard: ScorecardType,
  status: 'draft' | 'submitted'
): ScorecardSubmitPayload => {
  const questions = (scorecard?.questions ?? []).map((item, idx) => {
    const questionObj: ScorecardSubmitQuestion = { question: item?.question };
    const answer = values?.[idx + 1];
    if (answer)
      questionObj.answer = answer as ScorecardSubmitQuestion['answer'];
    return questionObj;
  });

  return {
    rating: values?.rating,
    feedback: values?.feedback,
    status,
    questions,
  };
};
